/** @jsxImportSource @emotion/react */
import { useGetPatientAgingQuery, useGetPatientBalancesQuery } from 'store/services/patientTransactions'

type PatientFinancialSummaryProps = {
  patientId: number
}

const formatCurrency = (amt: number) => {
  return `$${amt.toFixed(2)}`
}

export const PatientFinancialSummary = ({ patientId }: PatientFinancialSummaryProps) => {
  const { data: aging, isLoading: agingLoading } = useGetPatientAgingQuery({ patientId })
  const { data: balances, isLoading: balancesLoading } = useGetPatientBalancesQuery({ patientId })

  const isLoading = agingLoading || balancesLoading

  if (isLoading) {
    return (
      <div css={{ padding: '2rem', textAlign: 'center' }}>
        Loading financial summary...
      </div>
    )
  }

  const patientBalance = balances?.patientBalance ?? 0
  const planBalance = balances?.planBalance ?? 0
  const totalBalance = patientBalance + planBalance
  const totalPatientCharges = balances?.totalPatientCharges || 0
  const totalStatementBalance = balances?.totalStatementBalance || 0
  const lastPayment = balances?.lastPayment || 0

  return (
    <div>
      <div
        className={'d-flex mt-4'}
        css={{
          gap: 40,
          '& > div': {
            width: 300,
          },
          '& .balance-title': {
            fontSize: 18,
            fontWeight: 600,
            marginBottom: 20,
          },
          '& .balances': {
            '& > div': {
              display: 'flex',
              justifyContent: 'space-between',
              padding: '10px 0',
              borderBottom: '1px solid #edeef0',
              '& > div': {
                fontSize: '16px !important',
                fontWeight: 400,
                '&:nth-of-type(2)': {
                  fontWeight: 600,
                },
              },
            },
          },
        }}>
        <div>
          <div className={'balance-title'}>Balances</div>
          <div className={'balances'}>
            <div>
              <div>Total</div>
              <div className={'text-danger'}>
                {formatCurrency(totalBalance)}
              </div>
            </div>
            <div>
              <div>Patient</div>
              <div className={'text-success'}>
                {formatCurrency(patientBalance)}
              </div>
            </div>
            <div>
              <div>Plan</div>
              <div className={'text-danger'}>
                {formatCurrency(planBalance)}
              </div>
            </div>
          </div>
        </div>
        <div>
          <div className={'balance-title'}>Transaction History</div>
          <div className={'balances'}>
            <div>
              <div>Total Patient Charges</div>
              <div>{formatCurrency(totalPatientCharges)}</div>
            </div>
            <div>
              <div>Total Statement Balance</div>
              <div className={'text-danger'}>
                {formatCurrency(totalStatementBalance)}
              </div>
            </div>
            <div>
              <div>Last Payment</div>
              <div>{formatCurrency(lastPayment)}</div>
            </div>
          </div>
        </div>
      </div>
      <div
        css={{
          gap: 40,
          marginTop: 30,
          paddingTop: 30,
          borderTop: '1px solid #c4c8cf',
          '& .aging-title': {
            fontSize: 18,
            fontWeight: 600,
            marginBottom: 20,
          },
          '& .row': {
            padding: '10px 0',
            borderBottom: '1px solid #edeef0',
            '&:first-of-type': {
              fontWeight: 600,
              borderBottom: '1px solid #bdbdbd',
            },
            '& > div': {
              fontSize: '16px !important',
            },
          },
        }}>
        <div className={'aging-title'}>Aging</div>
        <div>
          <div className="row">
            <div className="col">Balance</div>
            <div className="col">Current</div>
            <div className="col">30+</div>
            <div className="col">60+</div>
            <div className="col">90+</div>
            <div className="col">120+</div>
            <div className="col">150+</div>
            <div className="col">180+</div>
          </div>
          <div className="row">
            <div className="col">Patient Balance</div>
            <div className="col">{formatCurrency(aging?.patientBalance.current || 0)}</div>
            <div className="col">{formatCurrency(aging?.patientBalance.days30 || 0)}</div>
            <div className="col">{formatCurrency(aging?.patientBalance.days60 || 0)}</div>
            <div className="col">{formatCurrency(aging?.patientBalance.days90 || 0)}</div>
            <div className="col">{formatCurrency(aging?.patientBalance.days120 || 0)}</div>
            <div className="col">{formatCurrency(aging?.patientBalance.days150 || 0)}</div>
            <div className="col">{formatCurrency(aging?.patientBalance.days180Plus || 0)}</div>
          </div>
          <div className="row">
            <div className="col">Plan Balance</div>
            <div className="col">{formatCurrency(aging?.planBalance.current || 0)}</div>
            <div className="col">{formatCurrency(aging?.planBalance.days30 || 0)}</div>
            <div className="col">{formatCurrency(aging?.planBalance.days60 || 0)}</div>
            <div className="col">{formatCurrency(aging?.planBalance.days90 || 0)}</div>
            <div className="col">{formatCurrency(aging?.planBalance.days120 || 0)}</div>
            <div className="col">{formatCurrency(aging?.planBalance.days150 || 0)}</div>
            <div className="col">{formatCurrency(aging?.planBalance.days180Plus || 0)}</div>
          </div>
          <div className="row">
            <div className="col">Total Balance</div>
            <div className="col">{formatCurrency(aging?.totalBalance.current || 0)}</div>
            <div className="col">{formatCurrency(aging?.totalBalance.days30 || 0)}</div>
            <div className="col">{formatCurrency(aging?.totalBalance.days60 || 0)}</div>
            <div className="col">{formatCurrency(aging?.totalBalance.days90 || 0)}</div>
            <div className="col">{formatCurrency(aging?.totalBalance.days120 || 0)}</div>
            <div className="col">{formatCurrency(aging?.totalBalance.days150 || 0)}</div>
            <div className="col">{formatCurrency(aging?.totalBalance.days180Plus || 0)}</div>
          </div>
        </div>
      </div>
    </div>
  )
}
